import { getSharedTool } from './sharedToolCatalog'

const registry = new Map()
const aliasRegistry = new Map()
const canonicalAliases = new Map()

function normalizeToolName(name) {
  return String(name || '').trim()
}

function resolveCanonicalName(name) {
  const normalized = normalizeToolName(name)
  if (!normalized) return null
  if (registry.has(normalized)) return normalized
  if (aliasRegistry.has(normalized)) return aliasRegistry.get(normalized)

  const sharedTool = getSharedTool(normalized)
  if (sharedTool?.name && registry.has(sharedTool.name)) {
    return sharedTool.name
  }

  return null
}

export function registerTool({ name, description, schema, handler, kind = 'read', aliases = [] }) {
  const canonicalName = normalizeToolName(name)
  if (!canonicalName || typeof handler !== 'function') throw new Error('Invalid tool registration')

  const previousAliases = canonicalAliases.get(canonicalName) || []
  for (const alias of previousAliases) {
    if (aliasRegistry.get(alias) === canonicalName) {
      aliasRegistry.delete(alias)
    }
  }

  const normalizedAliases = Array.from(
    new Set(
      (Array.isArray(aliases) ? aliases : [])
        .map((alias) => normalizeToolName(alias))
        .filter((alias) => alias && alias !== canonicalName)
    )
  )

  for (const alias of normalizedAliases) {
    if (registry.has(alias) && alias !== canonicalName) {
      throw new Error(`Tool alias conflicts with canonical tool name: ${alias}`)
    }
    const existingCanonical = aliasRegistry.get(alias)
    if (existingCanonical && existingCanonical !== canonicalName) {
      throw new Error(`Tool alias already registered: ${alias}`)
    }
  }

  registry.set(canonicalName, {
    name: canonicalName,
    description,
    schema,
    handler,
    kind,
    aliases: normalizedAliases
  })
  canonicalAliases.set(canonicalName, normalizedAliases)

  for (const alias of normalizedAliases) {
    aliasRegistry.set(alias, canonicalName)
  }
}

export function listTools() {
  return Array.from(registry.values())
}

export function listToolsForOpenRouter() {
  // Convert to OpenAI-style tool schema
  return listTools().map((t) => ({
    type: 'function',
    function: {
      name: t.name,
      description: t.description || '',
      parameters: t.schema || { type: 'object', properties: {} }
    }
  }))
}

export async function executeTool(name, args) {
  const resolvedName = resolveCanonicalName(name)
  const t = resolvedName ? registry.get(resolvedName) : null
  if (!t) throw new Error(`Unknown tool: ${name}`)
  return t.handler(args || {})
}

export function getTool(name) {
  const resolvedName = resolveCanonicalName(name)
  return resolvedName ? registry.get(resolvedName) || null : null
}

export function resetToolRegistry() {
  registry.clear()
  aliasRegistry.clear()
  canonicalAliases.clear()
}
